import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import {
  useGetDialogQuery,
  usePostMessageMutation,
  useGetFollowUpQuery,
  useGetScoreQuery,
} from "../../dialogs/dialogsSlice";
import { useDispatch } from "react-redux";
import { openRightPanel, closeRightPanel } from "../../../app/layoutSlice";
import useCreateDialog from "../../dialogs/hooks/useCreateDialog";

const useChatPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { dialogId } = useParams();
  const [isFinished, setIsFinished] = useState(false);
  const [isFreshFinish, setIsFreshFinish] = useState(false);
  const statusRef = useRef();
  const [messageCounter, setMessageCounter] = useState();
  const [messages, setMessages] = useState();
  const { postNewDialog, creatingDialog } = useCreateDialog();

  const currentDialog = useGetDialogQuery(
    { dialogId, from: messageCounter },
    { skip: dialogId == null || dialogId === "new" || messageCounter == null }
  );
  const followUp = useGetFollowUpQuery(
    { dialogId },
    { skip: dialogId == null || dialogId === "new" || !isFinished }
  );
  /*
  console.log(">> --------------------");
  console.log(">> dialogId: ", dialogId);
  console.log(">> isFreshFinish: ", isFreshFinish);
  console.log(
    ">> skip: ",
    dialogId == null || dialogId === "new" || !isFreshFinish
  );
  */
  const {
    data: score,
    status: scoreStatus,
    refetch: refetchScore,
  } = useGetScoreQuery(
    { dialogId },
    {
      skip: dialogId == null || dialogId === "new" || !isFreshFinish,
    }
  );
  // console.log(">> score: ", score);

  const [postMessageMutation, { isLoading: responseLoading }] =
    usePostMessageMutation();

  const postNewMessage = async ({ text }) => {
    let result;
    try {
      result = await postMessageMutation({
        params: { dialogId },
        body: { text },
      });
      if (result?.data?.last_entry != null) {
        setMessageCounter(result?.data?.last_entry);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleSubmit = (text) => {
    setMessages((state) => {
      const newMessage = {
        text,
        id: { dialog_id: dialogId, no: state.length + 1 },
        role: "USER",
      };
      if (state === null) {
        return [newMessage];
      } else {
        return [...state, newMessage];
      }
    });
    postNewMessage({ text });
  };

  let options = [];
  const optionLabels = {
    next: t("chat.nextProblem"),
    similar: t("chat.morePractice"),
    easier: t("chat.goEasier"),
    harder: t("chat.levelUp"),
  };
  const capitalize = (str) => str[0].toUpperCase() + str.slice(1);
  if (followUp?.data) {
    options = Object.entries(followUp?.data)
      .filter(([_, value]) => value)
      .map(([key]) => ({
        id: key,
        label: optionLabels[key] || capitalize(key),
      }));
  }

  const onCreateDialog = ({ dialogId, followUp }) => {
    statusRef.current = null;
    setMessages([]);
    setMessageCounter(null);
    setIsFinished(false);
    setIsFreshFinish(false);
    postNewDialog({ dialogId, followUp });
  };

  useEffect(() => {
    if (currentDialog?.status !== "fulfilled") return;
    const newMessages = currentDialog?.data?.messages ?? [];
    setMessages((prevMessages) => {
      const combined = [...(prevMessages || []), ...newMessages];
      const uniqueMessages = Array.from(
        new Map(
          combined.map((msg) => [`${msg.id.dialog_id}-${msg.id.no}`, msg])
        ).values()
      );
      return uniqueMessages.sort((a, b) => a.id.no - b.id.no);
    });
    if (!!currentDialog?.data?.canvas) {
      dispatch(openRightPanel({ content: "canvas" }));
    }
    // eslint-disable-next-line
  }, [currentDialog?.status]);

  useEffect(() => {
    console.log(
      ">>-->> currentDialog?.data?.status: ",
      currentDialog?.data?.status
    );
    if (currentDialog?.data?.status == null) return;
    if (currentDialog?.data?.status?.toLowerCase() === "finished") {
      console.log(">>-->> setIsFinished");
      setIsFinished(true);
      console.log(">>-->> statusRef.current: ", statusRef.current);
      if (
        statusRef.current != null &&
        statusRef.current !== currentDialog?.data?.status
      ) {
        console.log(">>-->> setIsFreshFinish");
        setIsFreshFinish(true);
      }
    }
    statusRef.current = currentDialog?.data?.status;
    console.log(">>-->> statusRef.current: ", statusRef.current);
  }, [currentDialog?.data?.status]);

  useEffect(() => {
    // statusRef.current = null;
    setMessages([]);
    setMessageCounter(0);
    return () => {
      dispatch(closeRightPanel());
    };
    // eslint-disable-next-line
  }, [dialogId]);

  useEffect(() => {
    if (messageCounter === 0 && !currentDialog?.isError) currentDialog?.refetch();
    // eslint-disable-next-line
  }, [messageCounter]);

  useEffect(() => {
    if (scoreStatus !== "fulfilled") return;
    if (score?.status !== "processing") return;

    const id = setInterval(() => {
      refetchScore();
    }, 500);

    // stop polling when deps change or component unmounts
    return () => clearInterval(id);
  }, [scoreStatus, score?.status, refetchScore]);

  return {
    messages,
    dialogStatus: currentDialog?.data?.status,
    onSubmit: handleSubmit,
    onCreateDialog,
    creatingDialog,
    composerEnabled: currentDialog?.data?.status === "READY",
    waiting:
      currentDialog?.isLoading ||
      responseLoading ||
      followUp?.isLoading ||
      (isFreshFinish && score?.status === "processing"),
    followUps: options,
    score: isFreshFinish && scoreStatus === "fulfilled" ? score : null,
  };
};

export default useChatPage;
